import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Package, Wrench, Calendar, CalendarDays, Repeat, 
  FileText, Heart, ArrowLeft, LayoutGrid
} from 'lucide-react';

const listingTypes = [
  {
    value: 'product',
    label: 'Product',
    description: 'Physical or digital products for sale',
    icon: Package,
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400'
  },
  {
    value: 'service',
    label: 'Service',
    description: 'Professional services, consultations, appointments',
    icon: Wrench,
    color: 'bg-green-50 border-green-200 hover:border-green-400'
  },
  {
    value: 'rental',
    label: 'Rental',
    description: 'Equipment, vehicles, or space for rent',
    icon: Calendar,
    color: 'bg-purple-50 border-purple-200 hover:border-purple-400'
  },
  {
    value: 'catalog',
    label: 'Catalog',
    description: 'One cohesive offering with multiple selectable items — hotel room types, a restaurant menu, or a product bundle',
    icon: LayoutGrid,
    color: 'bg-indigo-50 border-indigo-200 hover:border-indigo-400'
  },
  {
    value: 'event',
    label: 'Event',
    description: 'Workshops, classes, or ticketed events',
    icon: CalendarDays,
    color: 'bg-orange-50 border-orange-200 hover:border-orange-400'
  },
  {
    value: 'subscription',
    label: 'Subscription',
    description: 'Recurring products or services',
    icon: Repeat,
    color: 'bg-teal-50 border-teal-200 hover:border-teal-400'
  },
  {
    value: 'classified',
    label: 'Classified',
    description: 'One-time listings, second-hand items',
    icon: FileText,
    color: 'bg-gray-50 border-gray-200 hover:border-gray-400'
  },
  {
    value: 'volunteering',
    label: 'Volunteering',
    description: 'Volunteer opportunities and causes',
    icon: Heart,
    color: 'bg-pink-50 border-pink-200 hover:border-pink-400'
  }
];

const ListingTypeSelector = () => {
  const navigate = useNavigate();

  const handleSelect = (type) => {
    navigate(`/create-listing/${type}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create a New Listing
          </h1>
          <p className="text-gray-600">
            Choose the type of listing you want to create
          </p>
        </div>

        {/* Listing Type Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listingTypes.map((type) => {
            const Icon = type.icon;
            return (
              <Card 
                key={type.value}
                className={`cursor-pointer transition-all duration-200 ${type.color} hover:shadow-md`}
                onClick={() => handleSelect(type.value)}
                data-testid={`listing-type-${type.value}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/80">
                      <Icon className="h-6 w-6 text-gray-700" />
                    </div>
                    <CardTitle className="text-lg">{type.label}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">
                    {type.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Not sure which type to choose? <a href="/help/listing-types" className="text-blue-600 hover:underline">Learn more about listing types</a></p>
        </div>
      </div>
    </div>
  );
};

export default ListingTypeSelector;
